import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import moment from 'moment';
import { ApiService } from '../provider/api.service';
import { ConfigService } from '../provider/config.service';
import { AuthService } from './auth.service';
import { CacheManagementService } from './cache-management.service';
import { endpoints } from '../constant/endpoints';
export interface DayConfig {
  date: string;
  isDoubleBox: boolean;
  is_variable_carbs: boolean;
  isForceFixedMenu: boolean;
  selectionLimit: { [foodTypeId: number]: number } | any[];
  selectedSubscriptionID: number;
  mealsCount: number;
  snacksCount: number;
  min_calories: number;
  max_calories: number;
  max_scaled_carbs: number;
  default_scaled_carbs: number;
  weekDayOff: number;
  weekDay: string;
  subscriptLastDate: string;
}
export interface SubscriptionData {
  id: number;
  customer_id: number;
  start_date: string;
  end_date: string;
  status: string;
  freeze_dates: string[];
  double_box_dates: string[];
  used_freeze_count?: number;
  detail: {
    day_off: number | number[];
    total_meals: number;
    total_snacks: number;
    minimum_calories: number;
    maximum_calories: number;
    maximum_carbs: number;
    default_carbs: number;
    is_variable_carbs: boolean;
    force_fixed_menu: boolean;
    allowed_freeze_days?: number;
    selection_limit: any[];
  };
  particulars?: any[];
}
export interface DateServiceConfig {
  menu_selection_before: number;
  package_start_after: number;
  week_day_off: number;
}
export interface SubscriptionDateState {
  availableDates: string[];
  dayConfigs: { [date: string]: DayConfig };
  minDate: string;
  maxDate: string;
  minDateForCheckout: string;
  maxDateForCheckout: string;
  freezeDates: string[];
  activeSubscriptions: SubscriptionData[];
  activeDateRanges: Array<{ start: string; end: string }>;
  isLoading: boolean;
  error: string | null;
  lastUpdated: number;
}
@Injectable({
  providedIn: 'root'
})
export class SubscriptionDateService { 
  private readonly weekDayArray = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  private stateSubject = new BehaviorSubject<SubscriptionDateState>({
    availableDates: [],
    dayConfigs: {},
    minDate: moment().format('YYYY-MM-DD'),
    maxDate: moment().add(1, 'years').format('YYYY-MM-DD'),
    minDateForCheckout: moment().format('YYYY-MM-DD'),
    maxDateForCheckout: moment().add(1, 'years').format('YYYY-MM-DD'),
    freezeDates: [],
    activeSubscriptions: [],
    activeDateRanges: [],
    isLoading: false,
    error: null,
    lastUpdated: 0
  });
  private readonly CONFIG_CACHE_KEY = 'subscription-config';
  private customerId?: number;
  private initializationInProgress = false;
  public state$: Observable<SubscriptionDateState> = this.stateSubject.asObservable();
  public availableDates$: Observable<string[]> = this.state$.pipe(map(s => s.availableDates));
  public dayConfigs$: Observable<{ [date: string]: DayConfig }> = this.state$.pipe(map(s => s.dayConfigs));
  public minDate$: Observable<string> = this.state$.pipe(map(s => s.minDate));
  public maxDate$: Observable<string> = this.state$.pipe(map(s => s.maxDate));
  public minDateForCheckout$: Observable<string> = this.state$.pipe(map(s => s.minDateForCheckout));
  public maxDateForCheckout$: Observable<string> = this.state$.pipe(map(s => s.maxDateForCheckout));
  public freezeDates$: Observable<string[]> = this.state$.pipe(map(s => s.freezeDates));
  public activeSubscriptions$: Observable<SubscriptionData[]> = this.state$.pipe(map(s => s.activeSubscriptions));
  public isLoading$: Observable<boolean> = this.state$.pipe(map(s => s.isLoading));
  private configService?: ConfigService;
  constructor(
    private apiService: ApiService,
    private injector: Injector,
    private authService: AuthService,
    private cacheService: CacheManagementService
  ) {
    setTimeout(() => {
      this.configService = this.injector.get(ConfigService);
    });
  }
  /**
   * Initialize the service with customer data
   * This should be called once when the user logs in or when subscription data is needed
   */
  async initialize(customerId: number, forceRefresh: boolean = false): Promise<void> {
    const caller = new Error().stack?.split('\n')[2]?.trim() || 'unknown';
    if (!customerId) {
      console.warn('SubscriptionDateService: Cannot initialize without customer ID');
      return;
    }
    if (this.initializationInProgress) {
      return;
    }
    const currentState = this.stateSubject.value;
    const hasCachedData = currentState.activeSubscriptions.length > 0;
    const config = this.cacheService.getConfig('subscription-date');
    const isCacheValid = this.customerId === customerId && 
                        !forceRefresh && 
                        currentState.lastUpdated > 0 &&
                        (Date.now() - currentState.lastUpdated) < config.ttl &&
                        hasCachedData; 
    if (isCacheValid) {
      return;
    }
    if (!hasCachedData && currentState.lastUpdated > 0) {
      console.warn('[SubscriptionDateService] Cache is empty, forcing API call');
    }
    try {
      this.initializationInProgress = true;
      this.customerId = customerId;
      await this.loadConfig();
      await this.refreshSubscriptionData();
      this.cacheService.startAutoRefresh('subscription-date', async () => {
        if (this.customerId) {
          await this.initialize(this.customerId, true);
        }
      });
    } catch (error) {
      console.error('[SubscriptionDateService] Initialization error:', error);
      throw error;
    } finally {
      this.initializationInProgress = false;
    }
  }
  /**
   * Force refresh subscription data from API
   */
  async refreshSubscriptionData(): Promise<void> {
    this.updateState({ isLoading: true, error: null });
    try {
      const subscriptions = await this.fetchSubscriptions(this.customerId);
      const subscriptionsWithParticulars = await this.enrichSubscriptionsWithParticulars(subscriptions, this.customerId);
      const config = this.configCache!;
      const availableDates = this.calculateAvailableDates(subscriptionsWithParticulars, config);
      const dayConfigs = this.generateDayConfigs(subscriptionsWithParticulars, availableDates);
      const freezeDates = this.extractAllFreezeDates(subscriptionsWithParticulars);
      const activeDateRanges = this.extractActiveDateRanges(subscriptionsWithParticulars);
      const { minDate, maxDate } = this.calculateMinMaxDates(subscriptionsWithParticulars, config);
      const { minDateForCheckout, maxDateForCheckout } = this.calculateCheckoutMinMaxDates(subscriptionsWithParticulars, config, activeDateRanges);
      this.updateState({
        availableDates,
        dayConfigs,
        minDate,
        maxDate,
        minDateForCheckout,
        maxDateForCheckout,
        freezeDates,
        activeSubscriptions: subscriptionsWithParticulars,
        activeDateRanges,
        isLoading: false,
        lastUpdated: Date.now()
      });
      const currentState = this.stateSubject.value;
    } catch (error) {
      console.error('SubscriptionDateService: Error refreshing data', error);
      this.updateState({
        isLoading: false,
        error: 'Failed to load subscription data'
      });
      throw error;
    }
  }
  /**
   * Get current state snapshot
   */
  getState(): SubscriptionDateState {
    return this.stateSubject.value;
  }
  /**
   * Check if a specific date is available for selection (menu selection)
   */
  isDateAvailable(date: string): boolean {
    const state = this.stateSubject.value;
    return state.availableDates.includes(date);
  }
  /**
   * Check if a specific date is available for checkout (new subscription start date)
   */
  isDateAvailableForCheckout(date: string): boolean {
    const state = this.stateSubject.value;
    const dateMoment = moment(date);
    if (dateMoment.isBefore(moment(state.minDateForCheckout)) || 
        dateMoment.isAfter(moment(state.maxDateForCheckout))) {
      return false;
    }
    for (const range of state.activeDateRanges) {
      const startMoment = moment(range.start);
      const endMoment = moment(range.end);
      if (dateMoment.isBetween(startMoment, endMoment, undefined, '[]')) {
        return false;
      }
    }
    if (this.isDateDayOff(date)) {
      return false;
    }
    return true;
  }
  /**
   * Get configuration for a specific date
   */
  getDateConfig(date: string): DayConfig | null {
    const state = this.stateSubject.value;
    return state.dayConfigs[date] || null;
  }
  /**
   * Get the next available date after a given date, skipping N active days
   */
  getNextAvailableDate(fromDate: string, skipActiveDays: number = 0): string | null {
    const state = this.stateSubject.value;
    const config = this.configCache;
    if (!config) return null;
    let currentDate = moment(fromDate);
    let activeDaysCount = 0;
    const maxIterations = 365;
    let iterations = 0;
    while (activeDaysCount < skipActiveDays && iterations < maxIterations) {
      currentDate = currentDate.add(1, 'day');
      iterations++;
      const dateString = currentDate.format('YYYY-MM-DD');
      const dayOfWeek = currentDate.day();
      const isDayOff = dayOfWeek === config.week_day_off;
      const isFrozen = state.freezeDates.includes(dateString);
      const isAvailable = state.availableDates.includes(dateString);
      if (isAvailable && !isDayOff && !isFrozen) {
        activeDaysCount++;
      }
    }
    const resultDate = currentDate.format('YYYY-MM-DD');
    return state.availableDates.includes(resultDate) ? resultDate : null;
  }
  /**
   * Generate array of dates between start and end
   */
  getDateRange(startDate: string, endDate: string, format: string = 'DD MMMM, YYYY'): string[] {
    const dates: string[] = [];
    const currentDate = moment(startDate, format);
    const endMoment = moment(endDate, format);
    if (!currentDate.isValid() || !endMoment.isValid()) {
      console.warn('SubscriptionDateService: Invalid date format', { startDate, endDate, format });
      return dates;
    }
    while (currentDate.isSameOrBefore(endMoment)) {
      dates.push(currentDate.format('YYYY-MM-DD'));
      currentDate.add(1, 'day');
    }
    return dates;
  }
  /**
   * Filter dates for a specific subscription
   */
  filterDatesBySubscription(dates: string[], subscriptionId: number): string[] {
    const state = this.stateSubject.value;
    const subscription = state.activeSubscriptions.find(s => s.id === subscriptionId);
    if (!subscription) return [];
    const startMoment = moment(subscription.start_date, 'DD MMMM, YYYY');
    const endMoment = moment(subscription.end_date, 'DD MMMM, YYYY');
    const freezeSet = new Set(subscription.freeze_dates);
    const dayOffSet = new Set(this.normalizeDayOff(subscription.detail.day_off));
    return dates.filter(date => {
      const dateMoment = moment(date);
      const momentDayOfWeek = dateMoment.day(); 
      return dateMoment.isBetween(startMoment, endMoment, undefined, '[]') &&
             !freezeSet.has(date) &&
             !dayOffSet.has(momentDayOfWeek);
    });
  }
  /**
   * Get all dates for a specific address from subscription particulars
   */
  getAddressDates(addressId: number): string[] {
    const state = this.stateSubject.value;
    const addressDates: string[] = [];
    state.activeSubscriptions.forEach(subscription => {
      const particulars = subscription.particulars || [];
      particulars
        .filter((item: any) => item.address_id === addressId)
        .forEach((item: any) => {
          if (item.date && moment(item.date, 'YYYY-MM-DD', true).isValid()) {
            addressDates.push(moment(item.date).format('YYYY-MM-DD'));
          }
        });
    });
    return [...new Set(addressDates)].sort((a, b) => moment(a).diff(moment(b)));
  }
  /**
   * Check if a date is frozen
   */
  isDateFrozen(date: string): boolean {
    const state = this.stateSubject.value;
    return state.freezeDates.includes(date);
  }
  /**
   * Check if a date is a day-off (uses global week_day_off)
   * Note: This checks global day-off only. Subscription-specific day-offs are handled in date filtering.
   * Global day mapping: 0 = no day off, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat, 7=Sun
   */
  isDateDayOff(date: string): boolean {
    const config = this.configCache;
    if (!config || config.week_day_off === 0) return false; 
    const momentDayOfWeek = moment(date).day(); 
    const customDayOfWeek = momentDayOfWeek === 0 ? 7 : momentDayOfWeek; 
    return customDayOfWeek === config.week_day_off;
  }
  /**
   * Check if a date is a day-off for a specific subscription
   * Subscription day mapping: 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat (moment.js standard)
   * Global day mapping: 0 = no day off, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat, 7=Sun
   */
  isDateDayOffForSubscription(date: string, subscriptionId: number): boolean {
    const state = this.stateSubject.value;
    const subscription = state.activeSubscriptions.find(s => s.id === subscriptionId);
    if (!subscription) {
      return this.isDateDayOff(date);
    }
    const momentDayOfWeek = moment(date).day(); 
    const subscriptionDayOff = this.normalizeDayOff(subscription.detail.day_off);
    if (subscriptionDayOff.length > 0) {
      return subscriptionDayOff.includes(momentDayOfWeek);
    }
    return this.isDateDayOff(date);
  }
  /**
   * Get configuration settings
   */
  getConfig(): DateServiceConfig | null {
    return this.cacheService.get<DateServiceConfig>(this.CONFIG_CACHE_KEY, 'subscription-date');
  }
  clearCache(): void {
    this.cacheService.stopAutoRefresh('subscription-date');
    this.cacheService.clear('subscription-date');
    this.customerId = undefined;
    this.stateSubject.next({
      availableDates: [],
      dayConfigs: {},
      minDate: moment().format('YYYY-MM-DD'),
      maxDate: moment().add(1, 'years').format('YYYY-MM-DD'),
      minDateForCheckout: moment().format('YYYY-MM-DD'),
      maxDateForCheckout: moment().add(1, 'years').format('YYYY-MM-DD'),
      freezeDates: [],
      activeSubscriptions: [],
      activeDateRanges: [],
      isLoading: false,
      error: null,
      lastUpdated: 0
    });
  }
  /**
   * Get subscriptions as observable
   * This is the single source of truth for subscription data across the app
   * Other services should use this instead of making direct API calls
   */
  public getSubscriptions$(): Observable<any[]> {
    return this.activeSubscriptions$;
  }
  /**
   * Get current subscriptions synchronously
   * Use only when you're certain data is already loaded
   */
  public getSubscriptionsSnapshot(): any[] {
    return this.stateSubject.value.activeSubscriptions;
  }
  private async loadConfig(): Promise<void> {
    try {
      if (!this.configService) {
        this.configService = this.injector.get(ConfigService);
      }
      const orgData = await this.configService.configurationsSync();
      const settings = orgData?.organization?.configurations?.settings;
      const config: DateServiceConfig = {
        menu_selection_before: settings?.menu_selection_before ? Number(settings.menu_selection_before) : 3,
        package_start_after: settings?.package_start_after ? Number(settings.package_start_after) : 2,
        week_day_off: settings?.week_day_off ? Number(settings.week_day_off) : 5
      };
      this.cacheService.set(this.CONFIG_CACHE_KEY, config, 'subscription-date');
    } catch (error) {
      console.error('SubscriptionDateService: Error loading config', error);
      const defaultConfig: DateServiceConfig = {
        menu_selection_before: 3,
        package_start_after: 2,
        week_day_off: 5
      };
      this.cacheService.set(this.CONFIG_CACHE_KEY, defaultConfig, 'subscription-date');
    }
  }

  private get configCache(): DateServiceConfig | null {
    return this.cacheService.get<DateServiceConfig>(this.CONFIG_CACHE_KEY, 'subscription-date');
  }
  /**
   * Fetch particulars data from mySubscriptionsnew endpoint and merge into subscriptions
   */
  private async enrichSubscriptionsWithParticulars(
    subscriptions: SubscriptionData[], 
    customerId: number
  ): Promise<SubscriptionData[]> {
    try {
      const response: any = await this.apiService
        .get(`${endpoints.mySubscriptionsnew}?customer_id=${customerId}`)
        .toPromise();
      
      if (!response?.success || !response?.data?.subscriptions) {
        console.warn('[SubscriptionDateService] No particulars data available');
        return subscriptions;
      }
      
      const enrichmentMap = new Map<number, any>();
      
      response.data.subscriptions.forEach((sub: any) => {
        // Store the relevant data object using the subscription ID as the key
        if (sub.id) {
          enrichmentMap.set(sub.id, {
            particulars: sub.particulars,
            double_box_dates: Array.isArray(sub.double_box_dates) ? sub.double_box_dates : []
          });
        }
      });
      
      const enrichedSubscriptions = subscriptions.map(sub => {
        const enrichmentData = enrichmentMap.get(sub.id);
        
        if (enrichmentData) {
          const { particulars, double_box_dates } = enrichmentData;
          
          const newSubscription = {
            ...sub,
            // 1. Overwrite double_box_dates with data from the enrichment API
            double_box_dates: double_box_dates, 
          };
          
          // 2. Add particulars and macro details if they exist
          if (particulars) {
            // Note: This relies on your API returning a 'particulars' object 
            // even though your interface defines particulars as any[]
            Object.assign(newSubscription, {
              particulars: particulars,
              protein: particulars.protein || 0,
              calories: particulars.calories || 0,
              carbs: particulars.carbs || 0,
              fats: particulars.fats || 0
            });
          }
          
          return newSubscription;
        }
        
        return sub;
      });
      
      return enrichedSubscriptions;
    } catch (error) {
      console.error('[SubscriptionDateService] Error fetching particulars:', error);
      return subscriptions;
    }
  }
  // private async enrichSubscriptionsWithParticulars(
  //   subscriptions: SubscriptionData[], 
  //   customerId: number
  // ): Promise<SubscriptionData[]> {
  //   try {
  //     const response: any = await this.apiService
  //       .get(`${endpoints.mySubscriptionsnew}?customer_id=${customerId}`)
  //       .toPromise();
  //     if (!response?.success || !response?.data?.subscriptions) {
  //       console.warn('[SubscriptionDateService] No particulars data available');
  //       return subscriptions;
  //     }
  //     const particularsMap = new Map<number, any>();
  //     response.data.subscriptions.forEach((sub: any) => {
  //       if (sub.id && sub.particulars) {
  //         particularsMap.set(sub.id, sub.particulars);
  //       }
  //     });
  //     const enrichedSubscriptions = subscriptions.map(sub => {
  //       const particulars = particularsMap.get(sub.id);
  //       if (particulars) {
  //         return {
  //           ...sub,
  //           particulars,
  //           protein: particulars.protein || 0,
  //           calories: particulars.calories || 0,
  //           carbs: particulars.carbs || 0,
  //           fats: particulars.fats || 0
  //         };
  //       }
  //       return sub;
  //     });
  //     return enrichedSubscriptions;
  //   } catch (error) {
  //     console.error('[SubscriptionDateService] Error fetching particulars:', error);
  //     return subscriptions;
  //   }
  // }
  private async fetchSubscriptions(customerId: number): Promise<SubscriptionData[]> {
    try {
      const response: any = await this.apiService
        .get(`${endpoints.subData}?customer_id=${customerId}`)
        .toPromise();
      if (!response?.success || !response?.data?.subscriptions) {
        return [];
      }
      const activeSubscriptions: SubscriptionData[] = [];
      response.data.subscriptions.forEach((group: any) => {
        if (group.type_name === 'Active Subscription' && group.type_data) {
          group.type_data.forEach((sub: any) => {
            if (sub.status === 'activated') {
              activeSubscriptions.push(this.normalizeSubscription(sub));
            }
          });
        }
      });
      return activeSubscriptions;
    } catch (error) {
      console.error('SubscriptionDateService: Error fetching subscriptions', error);
      throw error;
    }
  }
  private normalizeSubscription(rawSub: any): SubscriptionData {
    return {
      id: rawSub.id,
      customer_id: rawSub.customer_id,
      start_date: rawSub.start_date,
      end_date: rawSub.end_date,
      status: rawSub.status,
      freeze_dates: Array.isArray(rawSub.freeze_dates) ? rawSub.freeze_dates : [],
      double_box_dates: Array.isArray(rawSub.double_box_dates) ? rawSub.double_box_dates : [],
      used_freeze_count: rawSub.used_freeze_count ?? 0,
      detail: {
        day_off: rawSub.detail?.day_off ?? 5,
        total_meals: rawSub.detail?.total_meals ?? 0,
        total_snacks: rawSub.detail?.total_snacks ?? 0,
        minimum_calories: rawSub.detail?.minimum_calories ?? 0,
        maximum_calories: rawSub.detail?.maximum_calories ?? 9999999,
        maximum_carbs: rawSub.detail?.maximum_carbs ?? 0,
        default_carbs: rawSub.detail?.default_carbs ?? 50,
        is_variable_carbs: rawSub.detail?.is_variable_carbs ?? false,
        force_fixed_menu: rawSub.detail?.force_fixed_menu ?? false,
        allowed_freeze_days: rawSub.detail?.allowed_freeze_days ?? 0,
        selection_limit: rawSub.detail?.selection_limit ?? []
      },
      particulars: rawSub.particulars || []
    };
  }
  private calculateAvailableDates(subscriptions: SubscriptionData[], config: DateServiceConfig): string[] {
    const today = moment().startOf('day');
    const allValidDates: string[] = [];
    subscriptions.forEach(subscription => {
      const { start_date, end_date, freeze_dates, detail } = subscription;
      const freezeDatesSet = new Set(freeze_dates);
      const subscriptionDayOff = this.normalizeDayOff(detail.day_off);
      const hasSubscriptionDayOff = subscriptionDayOff.length > 0;
      const hasGlobalDayOff = config.week_day_off !== 0;
      const dateRange = this.getDateRange(start_date, end_date);
      const subscriptionStartMoment = moment(start_date, 'DD MMMM, YYYY');
      const filteredDates = dateRange.filter(dateStr => {
        const mDate = moment(dateStr);
        const momentDayOfWeek = mDate.day(); 
        const customDayOfWeek = momentDayOfWeek === 0 ? 7 : momentDayOfWeek;
        let isDayOff = false;
        if (hasSubscriptionDayOff) {
          isDayOff = subscriptionDayOff.includes(momentDayOfWeek);
        } else if (hasGlobalDayOff) {
          isDayOff = customDayOfWeek === config.week_day_off;
        }
        if (freezeDatesSet.has(dateStr)) {
          return false;
        }
        if (isDayOff) {
          return false;
        }
        if (mDate.isBefore(today)) {
          return false;
        }
        const minSelectableDate = today.clone().add(config.menu_selection_before, 'days');
        if (subscriptionStartMoment.isAfter(today)) {
          const effectiveMinDate = moment.max(minSelectableDate, subscriptionStartMoment);
          const isValid = mDate.isSameOrAfter(effectiveMinDate);
          return isValid;
        } else {
          const isValid = mDate.isSameOrAfter(minSelectableDate);
          return isValid;
        }
      });
      allValidDates.push(...filteredDates);
    });
    return [...new Set(allValidDates)].sort((a, b) => moment(a).diff(moment(b)));
  }
  private generateDayConfigs(subscriptions: SubscriptionData[], dates: string[]): { [date: string]: DayConfig } {
    const dayConfigs: { [date: string]: DayConfig } = {};
    dates.forEach(date => {
      const currentDate = moment(date);
      for (const subscription of subscriptions) {
        const startDate = moment(subscription.start_date, 'DD MMMM, YYYY');
        const endDate = moment(subscription.end_date, 'DD MMMM, YYYY');
        if (currentDate.isBetween(startDate, endDate, undefined, '[]')) {
          const detail = subscription.detail;
          const lastDate = subscription.end_date ? moment(subscription.end_date, 'DD MMMM, YYYY').format('YYYY-MM-DD') : '';
          dayConfigs[date] = {
            date,
            isDoubleBox: subscription.double_box_dates?.includes(date) ?? false,
            is_variable_carbs: detail.is_variable_carbs || false,
            isForceFixedMenu: detail.force_fixed_menu || false,
            selectionLimit: detail.selection_limit || [],
            selectedSubscriptionID: subscription.id,
            mealsCount: Number(detail.total_meals) || 0,
            snacksCount: Number(detail.total_snacks) || 0,
            min_calories: Number(detail.minimum_calories) || 0,
            max_calories: Number(detail.maximum_calories) || 9999999,
            max_scaled_carbs: Number(detail.maximum_carbs) || 0,
            default_scaled_carbs: Number(detail.default_carbs) || 50,
            weekDayOff: this.getFirstDayOff(detail.day_off),
            weekDay: this.weekDayArray[this.getFirstDayOff(detail.day_off)] || '',
            subscriptLastDate: lastDate
          };
          break;
        }
      }
    });
    return dayConfigs;
  }
  private extractAllFreezeDates(subscriptions: SubscriptionData[]): string[] {
    const allFreezeDates: string[] = [];
    subscriptions.forEach(subscription => {
      if (Array.isArray(subscription.freeze_dates)) {
        allFreezeDates.push(...subscription.freeze_dates);
      }
    });
    return [...new Set(allFreezeDates)].sort((a, b) => moment(a).diff(moment(b)));
  }
  private calculateMinMaxDates(subscriptions: SubscriptionData[], config: DateServiceConfig): { minDate: string; maxDate: string } {
    const today = moment().startOf('day');
    let minDate = today.format('YYYY-MM-DD');
    let maxDate = moment().add(1, 'years').format('YYYY-MM-DD');
    if (subscriptions.length > 0) {
      let earliestStartDate: moment.Moment | null = null;
      let latestEndDate: moment.Moment | null = null;
      subscriptions.forEach(subscription => {
        const startDate = moment(subscription.start_date, 'DD MMMM, YYYY');
        const endDate = moment(subscription.end_date, 'DD MMMM, YYYY');
        if (!earliestStartDate || startDate.isBefore(earliestStartDate)) {
          earliestStartDate = startDate;
        }
        if (!latestEndDate || endDate.isAfter(latestEndDate)) {
          latestEndDate = endDate;
        }
      });
      if (earliestStartDate && earliestStartDate.isAfter(today)) {
        minDate = earliestStartDate.format('YYYY-MM-DD');
      } else {
        minDate = today.clone().add(config.menu_selection_before, 'days').format('YYYY-MM-DD');
      }
      if (latestEndDate) {
        maxDate = latestEndDate.format('YYYY-MM-DD');
      }
    } else {
      minDate = today.clone().add(config.menu_selection_before, 'days').format('YYYY-MM-DD');
    }
    return { minDate, maxDate };
  }
  private calculateCheckoutMinMaxDates(
    subscriptions: SubscriptionData[], 
    config: DateServiceConfig,
    activeDateRanges: Array<{ start: string; end: string }>
  ): { minDateForCheckout: string; maxDateForCheckout: string } {
    const today = moment().startOf('day');
    const minDateForCheckout = today.clone().add(config.package_start_after, 'days').format('YYYY-MM-DD');
    const maxDateForCheckout = moment().add(1, 'years').format('YYYY-MM-DD');
    return { minDateForCheckout, maxDateForCheckout };
  }
  private extractActiveDateRanges(subscriptions: SubscriptionData[]): Array<{ start: string; end: string }> {
    return subscriptions.map(subscription => ({
      start: moment(subscription.start_date, 'DD MMMM, YYYY').format('YYYY-MM-DD'),
      end: moment(subscription.end_date, 'DD MMMM, YYYY').format('YYYY-MM-DD')
    }));
  }
  private normalizeDayOff(dayOff: number | number[]): number[] {
    if (Array.isArray(dayOff)) {
      return dayOff.map(Number).filter(d => d >= 0 && d <= 6);
    }
    return (dayOff != null && dayOff >= 0 && dayOff <= 6) ? [Number(dayOff)] : [];
  }
  private getFirstDayOff(dayOff: number | number[]): number {
    const normalized = this.normalizeDayOff(dayOff);
    return normalized.length > 0 ? normalized[0] : null;
  }
  private updateState(partial: Partial<SubscriptionDateState>): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({ ...currentState, ...partial });
  }
}
